import React from "react";
import { drawTieTeams } from "./drawTieTeams";
import { Avatar } from "components/src/components/Avatar/Avatar";
import { Icon } from "components/src/components/Icon/Icon";
import { TeamLogo } from "../../TeamLogo/TeamLogo";
import { isValidIcon } from "shared/src/hooks/useImageResolver";
import { normalizeSport } from "shared/src/utils/sportMap";
import { type SizeType } from "shared/src/types";
import { type ThemeModeType } from "shared/src/types";
import { TeamLogoType } from "../../TeamLogo/types";
import { isUseableType } from "shared/src/utils/isUseableType";
import { JerseyNumber } from "../../JerseyNumber/JerseyNumber";
import { AvatarSlotConfig } from "components/src/components/Avatar/types";
import { sizeStyles } from "shared/src/styles/sizeStyles";
import { calcDivideBy } from "shared/src/utils/mathUtils";

export const resolveIcon = ({
  _avatar,
  showIcon,
  variant,
  country,
  iconSize,
  sport: _sportRaw,
  league,
  competitionType,
  mode,
  hasFoundTeam,
  _team,
  teamInfo,
  hasCountry,
  isFlagAllowed,
  isTeamCountry,
  icon,
  flipIcon,
  hasTeam,
  isDrawTeam,
  useAvatar,
  jerseyNumber,
  position,
  transform,
  logoType
}) => {
  let _iconSize: SizeType = variant === "vertical" ? "3xl" : "2xl";
  const sport = normalizeSport(_sportRaw);
  if ((country || isTeamCountry) && !iconSize && variant === "horizontal") _iconSize = "2xl";
  if (iconSize) _iconSize = iconSize;

  const hasAvatarSrc = _avatar && !!_avatar?.src;
  const hasValidIcon = icon && typeof icon === "string" ? isValidIcon(icon) : false;
  const hasClashingTeam = (teamInfo?.league === "soccer" || teamInfo?.league === "football") && sport !== "football";

  const _computedIconStyle = transform ? {
    transform: Object.entries(transform).map(([key, val])=>{ return `${key}(${val})`}).flat().join(" ")
  } : undefined;

  const iconProps: {
    name: string | undefined;
    size: SizeType;
    sport?: string;
    league?: string;
    competition?: string;
    competitionType?: string;
    skeleton?: boolean;
    mode?: ThemeModeType;
    flipIcon?: boolean;
    type?: TeamLogoType;
    style?: React.CSSProperties;
  } = {
    name: "",
    size: _iconSize,
    sport,
    league,
    competition: league,
    competitionType,
    mode,
    flipIcon,
    type: logoType,
    style: _computedIconStyle
  };

  if (hasFoundTeam && !hasValidIcon && !hasCountry) {
    if (teamInfo.league === "countries") {
      iconProps.name = teamInfo.metadata.images.primary.name;
    } else {
      iconProps.name = teamInfo?.name;
    }
  } else if (hasCountry) {
    iconProps.name =
      teamInfo?.metadata?.images?.primary?.name ?? "flag_placeholder";
  } else if (icon && isValidIcon(icon)) {
    iconProps.name = icon;
  } else if (drawTieTeams.includes(_team?.toLowerCase())) {
    iconProps.name = "icon_ui_neutral_ban";
  }

  const hasImage =
    (hasTeam && hasFoundTeam) ||
    isTeamCountry ||
    !!icon ||
    !!country ||
    isDrawTeam;

  const _useAvatar = useAvatar || !!jerseyNumber || !!position;
  let computedIcon: React.ReactNode;
  const [_icon, _teamLogoRaw] = [Icon(iconProps), TeamLogo(iconProps)]; //!isDrawTeam && hasFoundTeam ? TeamLogo(iconProps) : null];
  const _teamLogo = !isDrawTeam && hasFoundTeam ? _teamLogoRaw : null;
 
  let avatarIcon: React.ReactNode = null;
  if (_useAvatar && hasImage) {
    
    let iconForAvatar: React.ReactNode = _icon;
    if (hasFoundTeam && !isTeamCountry && !icon && !hasAvatarSrc && _teamLogo) {
      iconForAvatar = _teamLogo;
    }
    if ((isDrawTeam || hasCountry) && !icon && !hasAvatarSrc) {
      iconForAvatar = iconProps.name;
    }
    const avatarSize = _avatar?.size ?? "2xl";
    const { sizeMap: avatarSizeMap } = sizeStyles(avatarSize);
    const calculatedJerseyNumberSize = calcDivideBy(avatarSizeMap?.width, 2.2) || avatarSize;
    const [hasJerseyNumber, hasPosition] = [isUseableType(jerseyNumber), isUseableType(position)];
    const computedJerseyNumberSlot: AvatarSlotConfig = {
      slot: hasJerseyNumber ? <JerseyNumber colorType="dark" value={jerseyNumber} size={calculatedJerseyNumberSize} strokeInside={teamInfo?.metadata?.colors?.primary?.value} strokeOutside={teamInfo?.metadata?.colors?.secondary?.value} /> : null,
      showBorder: false,
      size: avatarSize,
      forceEdgeAlignment: !!logoType ? false : true
    };
    const computedPositionSlot = {
      slot: hasPosition ? <JerseyNumber colorType="dark" value={position} /> : null,
      showBorder: false
    }
    const computedAvatarProps = {..._avatar, showBorder: hasJerseyNumber || hasPosition ? false : _avatar?.showBorder }
    avatarIcon = (
      <Avatar
        src={_avatar?.src}
        icon={iconForAvatar}
        size={avatarSize}
        mode={mode}
        leadingSlot={computedJerseyNumberSlot}
        trailingSlot={computedPositionSlot}
        {...computedAvatarProps}
      />
    );
  }

  const isValidCountry = isTeamCountry || hasCountry;
  const flagOrPlaceholder =
    _icon && isValidCountry ? (
      _icon
    ) : (
      <Icon {...iconProps} name="flag_placeholder" />
    );

  // 'hasCountry' refers to using the `country` prop in CompetitorProps.   `isTeamCountry` computes if the found team from searching is a country 'team' e.g. 'Spain'.
  if (_teamLogo && !isValidCountry) {
    computedIcon = _teamLogo;
  } else if (isValidCountry) {
    computedIcon = flagOrPlaceholder;
  } else if ((icon || isDrawTeam) && _icon) {
    computedIcon = _icon;
  }
  const activeIcon = _useAvatar ? avatarIcon : computedIcon;
  let showComputedIcon = showIcon && hasImage && activeIcon && !hasClashingTeam;

  return { activeIcon, showComputedIcon, _iconSize };
};
